/**
 * @module Sagas
 * @description Saga file containing logic for side effects. Contains logic for making requests and running animations. It's tightly coupled with the action layer as it responds to actions and files it's own actions.
 */

/* global document */

import { delay } from 'redux-saga';
import {
  call,
  put,
  takeEvery,
  cancelled,
  fork,
  cancel,
  take
} from 'redux-saga/effects';
import { push } from 'react-router-redux';
import request from 'request';
import {
  SIGN_ANIMATIONS_STOP,
  SIGN_FETCH_REQUESTED,
  SIGN_FETCH_SUCCEEDED,
  SIGN_FETCH_FAILED,
  VENDOR_FETCH_REQUESTED,
  VENDOR_FETCH_SUCCEEDED,
  VENDOR_FETCH_FAILED,
  SEND_NUMBER_REQUESTED,
  SEND_NUMBER_SUCCEEDED,
  SEND_NUMBER_FAILED,
  SIGN_REDIRECT_START,
  SIGN_REDIRECT_STOP,
  SIGN_KEYPAD_KEYS_SHOW,
  signAnimationsCanceled,
  signLoaderModeUpdate,
  signRedirectStop,
  signRedirectStart,
  signKeypadModeUpdate,
  signReset
} from '../actions';

function readVendor(id) {
  return new Promise((resolve, reject) => {
    request(
      `${document.location.origin}/api/vendor/readWithId/${id}`,
      (error, response, body) => {
        if (error) {
          reject(error);
        } else {
          resolve(JSON.parse(body));
        }
      }
    );
  });
}

function readSign(id) {
  return new Promise((resolve, reject) => {
    request(
      `${document.location.origin}/api/sign/readWithId/${id}`,
      (error, response, body) => {
        if (error) {
          reject(error);
        } else {
          resolve(JSON.parse(body));
        }
      }
    );
  });
}

function postNumber(number) {
  return new Promise((resolve, reject) => {
    request.post(
      `${document.location.origin}/api/sms/send/${number}`,
      error => {
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      }
    );
  });
}

function* readVendorSaga(action) {
  const { id } = action.data;

  try {
    const data = yield call(readVendor, id);
    yield put({ type: VENDOR_FETCH_SUCCEEDED, data });
  } catch (e) {
    yield put({ type: VENDOR_FETCH_FAILED, message: e.message });
  }
}

function* readSignCancellableSaga(action) {
  const { id, redirect, duration } = action.data;

  try {
    const data = yield call(readSign, id);
    yield call(delay, 600);
    yield put(signLoaderModeUpdate(1));
    yield put({ type: SIGN_FETCH_SUCCEEDED, data });
    yield call(delay, 2000);
    yield put(signLoaderModeUpdate(2));
    yield call(delay, 1000);
    yield put(signLoaderModeUpdate(3));
    yield call(delay, 2000);
    yield put(signLoaderModeUpdate(4));
    yield call(delay, 600);
    yield put(signLoaderModeUpdate(5));
    yield call(delay, 600);
    yield put(signLoaderModeUpdate(6));
    yield call(delay, 1000);
    yield put(signLoaderModeUpdate(7));
    yield call(delay, 10000);
    yield put(signKeypadModeUpdate(1));
    yield call(delay, 1000);
    yield put(signKeypadModeUpdate(2));
    yield put(signRedirectStart({ redirect, duration }));
  } catch (e) {
    yield put({ type: SIGN_FETCH_FAILED, message: e.message });
  } finally {
    if (yield cancelled()) yield put(signAnimationsCanceled());
  }
}

function* signKeysShowSaga() {
  yield put(signKeypadModeUpdate(3));
  yield call(delay, 1000);
  yield put(signKeypadModeUpdate(4));
  yield call(delay, 1000);
  yield put(signKeypadModeUpdate(5));
}

function* postNumberSaga(action) {
  const { number, redirect } = action.data;

  yield put(signRedirectStop());

  try {
    const data = yield call(postNumber, number);
    yield put({ type: SEND_NUMBER_SUCCEEDED, data });
    yield put(signKeypadModeUpdate(6));
    yield call(delay, 1000);
    yield put(signKeypadModeUpdate(7));
    yield call(delay, 1000);
    yield put(signKeypadModeUpdate(8));
    yield call(delay, 1000);
    yield put(signKeypadModeUpdate(9));
    yield call(delay, 1000);
    yield put(signKeypadModeUpdate(10));
    yield put(signRedirectStart({ redirect, duration: 60000 }));
  } catch (e) {
    yield put({ type: SEND_NUMBER_FAILED, message: e.message });
  }
}

function* redirectCancellableSaga(action) {
  const { redirect, duration } = action.data;

  try {
    yield call(delay, duration);
    yield put(push(redirect));
    yield put(signReset());
  } finally {
    if (yield cancelled()) {
      yield put({ type: 'SIGN_REDIRECT_CANCELED' });
    }
  }
}

function* redirectSaga() {
  while (true) {
    const action = yield take(SIGN_REDIRECT_START);
    const backgroundTask = yield fork(
      redirectCancellableSaga.bind(null, action)
    );
    yield take(SIGN_REDIRECT_STOP);
    yield cancel(backgroundTask);
  }
}

function* readSignSaga() {
  while (true) {
    const action = yield take(SIGN_FETCH_REQUESTED);
    const backgroundTask = yield fork(
      readSignCancellableSaga.bind(null, action)
    );
    yield take(SIGN_ANIMATIONS_STOP);
    yield cancel(backgroundTask);
  }
}

function* saga() {
  yield takeEvery(VENDOR_FETCH_REQUESTED, readVendorSaga);
  yield takeEvery(SEND_NUMBER_REQUESTED, postNumberSaga);
  yield takeEvery(SIGN_KEYPAD_KEYS_SHOW, signKeysShowSaga);
  yield fork(readSignSaga);
  yield fork(redirectSaga);
}

export default saga;



// WEBPACK FOOTER //
// ./src/sagas/index.js